import { useMemo, useState } from 'react';
import { api, bodyOf, type Expanded, type Plan, type RuleRef, type RuleVersion } from '../lib/api.ts';
import { describeRule, shortDate, type Names } from '../lib/describe.ts';
import { addDays, addMonths, addYears, dateTimeIn, dayBounds, hours, timeIn, today } from '../lib/plantime.ts';
import { ErrorNote, Icon, useLoad } from '../ui.tsx';

interface Day {
  iso: string;
  measuredMs: number;
  lengthMs: number;
  override: boolean;
}

const WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function Calendar({ plan, names }: { plan: Plan; names: Names }) {
  const rules = useLoad(() => api.rules(plan.id), [plan.id]);
  const firstRule = rules.data?.map((r) => r.effectiveFrom).sort()[0];
  const [start, setStart] = useState<string | null>(null);
  // A year from the day the plan took effect, so the default view is the year the owner checks.
  const from = start ?? firstRule ?? today(plan.timeZone);
  const to = addDays(addYears(from, 1), -1);
  const data = useLoad(() => (rules.data ? api.expand(plan.id, from, to) : Promise.resolve(null)), [plan.id, from, rules.data]);
  const [picked, setPicked] = useState<string | null>(null);

  const measured = plan.measuredPartyId!;
  const measuredName = plan.parties.find((p) => p.id === measured)?.label ?? 'Measured parent';
  const exp = data.data;

  const days = useMemo(() => (exp ? dayMap(exp, from, to, plan.timeZone, measured) : new Map<string, Day>()), [exp, from, to, plan.timeZone, measured]);
  const months: string[] = [];
  for (let m = `${from.slice(0, 7)}-01`; m <= to; m = addMonths(m, 1)) months.push(m);

  const stated = plan.statedShares?.[measured];
  const label = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

  return (
    <>
      <div className="title-row">
        <div>
          <h1>Year calendar</h1>
          <p className="finding">
            {measuredName}’s scheduled time, as the plan sets it. Tap a day to see which rule decided it. Check it once against your own calendar.
          </p>
        </div>
        <div className="cal-tools">
          <button className="btn btn-ghost" aria-label="Previous year" onClick={() => setStart(addYears(from, -1))}>
            <Icon name="back" />
          </button>
          <span className="range">
            {shortDate(from)} – {shortDate(to)}
          </span>
          <button className="btn btn-ghost" aria-label="Next year" onClick={() => setStart(addYears(from, 1))}>
            <Icon name="next" />
          </button>
        </div>
      </div>

      <ErrorNote error={data.error ?? rules.error} />
      {exp && (
        <p className="share-line" style={{ marginBottom: 16 }}>
          {plan.parties.map((p) => (
            <span key={p.id}>
              {p.label} <b>{(exp.share[p.id] ?? 0).toFixed(1)}%</b> · {Math.round(exp.hours[p.id] ?? 0).toLocaleString()} h
            </span>
          ))}
          <span>
            {exp.visits.length} visits{stated != null ? ` · the plan states ${stated}% for ${measuredName}` : ''}
          </span>
        </p>
      )}

      <div className="cols">
        <section className="panel">
          <div className="cal-key">
            <span>
              <i className="sw" style={{ background: 'var(--primary)' }} /> {measuredName} all day
            </span>
            <span>
              <i className="sw" style={{ background: 'linear-gradient(to top, var(--plan) 50%, var(--raised) 50%)' }} /> Part of the day, by hours
            </span>
            <span>
              <i className="sw" style={{ border: '1.5px dashed var(--control)' }} /> A holiday, break or block overrides the regular schedule
            </span>
          </div>
          {data.loading && !exp ? <p className="empty">Working out the schedule…</p> : null}
          <div className="months">
            {months.map((m) => (
              <Month key={m} first={m} days={days} picked={picked} onPick={setPicked} title={label(m)} />
            ))}
          </div>
        </section>

        <aside className="panel detail" aria-live="polite">
          {picked && exp ? (
            <DayDetail iso={picked} exp={exp} plan={plan} names={names} versions={rules.data ?? []} />
          ) : (
            <p className="muted">Pick a day to see who has your child, when, and the clause behind it.</p>
          )}
        </aside>
      </div>
    </>
  );
}

function dayMap(exp: Expanded, from: string, to: string, tz: string, measured: string): Map<string, Day> {
  const ivs = exp.intervals.map((iv) => ({ ...iv, s: Date.parse(iv.start), e: Date.parse(iv.end) }));
  const out = new Map<string, Day>();
  let i = 0;
  for (let iso = from; iso <= to; iso = addDays(iso, 1)) {
    const { start, end } = dayBounds(iso, tz);
    while (i < ivs.length && ivs[i]!.e <= start) i++;
    let measuredMs = 0;
    let override = false;
    for (let j = i; j < ivs.length && ivs[j]!.s < end; j++) {
      const iv = ivs[j]!;
      const ms = Math.min(iv.e, end) - Math.max(iv.s, start);
      if (iv.party === measured) measuredMs += ms;
      if (iv.overridden.length) override = true;
    }
    out.set(iso, { iso, measuredMs, lengthMs: end - start, override });
  }
  return out;
}

function Month({ first, days, picked, onPick, title }: { first: string; days: Map<string, Day>; picked: string | null; onPick: (d: string) => void; title: string }) {
  const lead = new Date(`${first}T00:00:00Z`).getUTCDay();
  const cells: (string | null)[] = Array.from({ length: lead }, () => null);
  for (let iso = first; iso.slice(0, 7) === first.slice(0, 7); iso = addDays(iso, 1)) cells.push(iso);
  while (cells.length % 7) cells.push(null);
  const weeks = Array.from({ length: cells.length / 7 }, (_, w) => cells.slice(w * 7, w * 7 + 7));
  return (
    <div className="month">
      <h3>{title}</h3>
      <table>
        <thead>
          <tr>
            {WEEK.map((d) => (
              <th key={d} scope="col">
                {d.slice(0, 2)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((w, wi) => (
            <tr key={wi}>
              {w.map((iso, di) => {
                const d = iso ? days.get(iso) : undefined;
                if (!iso) return <td key={di} />;
                if (!d) {
                  return (
                    <td key={iso}>
                      <span className="day" data-out="true" aria-hidden="true">
                        {Number(iso.slice(8))}
                      </span>
                    </td>
                  );
                }
                const share = d ? d.measuredMs / d.lengthMs : 0;
                const has = share >= 0.999 ? 'all' : share > 0 ? 'part' : 'none';
                return (
                  <td key={iso}>
                    <button
                      className="day"
                      data-has={has}
                      data-override={d?.override ?? false}
                      aria-pressed={picked === iso}
                      aria-label={`${shortDate(iso)}${has === 'none' ? '' : `, ${hours(d!.measuredMs)} scheduled`}`}
                      style={{ ['--share' as string]: `${Math.round(share * 100)}%` }}
                      onClick={() => onPick(iso)}
                    >
                      {Number(iso.slice(8))}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DayDetail({ iso, exp, plan, names, versions }: { iso: string; exp: Expanded; plan: Plan; names: Names; versions: RuleVersion[] }) {
  const tz = plan.timeZone;
  const { start, end } = dayBounds(iso, tz);
  const ivs = exp.intervals.filter((iv) => Date.parse(iv.start) < end && Date.parse(iv.end) > start);
  const party = (id: string) => plan.parties.find((p) => p.id === id)?.label ?? 'Unknown';
  const find = (r: RuleRef) => versions.find((v) => v.ruleId === r.id && v.version === r.version);
  const sentence = (r: RuleRef) => {
    const v = find(r);
    return v ? describeRule(bodyOf(v), names) : r.clause;
  };
  const span = (s: number, e: number) => {
    const sameDay = s >= start && e <= end;
    return sameDay
      ? `${s === start ? '12:00 AM' : timeIn(s, tz)} – ${e === end ? '12:00 AM' : timeIn(e, tz)}`
      : `${dateTimeIn(s, tz)} → ${dateTimeIn(e, tz)}`;
  };
  return (
    <>
      <h2>{shortDate(iso)}</h2>
      {ivs.map((iv, i) => (
        <div className="iv" key={i}>
          <b>
            {party(iv.party)} · {span(Date.parse(iv.start), Date.parse(iv.end))}
          </b>
          {iv.rule ? (
            <span>
              {iv.rule.clause}: {sentence(iv.rule)}
            </span>
          ) : (
            <span className="muted">No rule applies, so the default parent has your child.</span>
          )}
          {iv.overridden.length > 0 && (
            <span className="muted">Overrides {iv.overridden.map((r) => `${r.clause} (${sentence(r)})`).join('; ')}</span>
          )}
        </div>
      ))}
      <p className="hint" style={{ marginTop: 12 }}>
        Times are in {tz.replace('_', ' ')}.
      </p>
    </>
  );
}
